import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './ETLDashboard.css';

const ETLDashboard = () => {
  usePageTitle('Dashboard');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axiosInstance.get('/etl/dashboard/');
      setOverview(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'badge-warning',
      extracting: 'badge-info',
      uploading: 'badge-info',
      processing: 'badge-info',
      loading: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-error',
      cancelled: 'badge-secondary',
    };
    return statusClasses[status] || 'badge-secondary';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  const successRate = overview.total_runs > 0 
    ? ((overview.successful_runs / overview.total_runs) * 100).toFixed(1)
    : 0;

  const failureRate = overview.total_runs > 0
    ? ((overview.failed_runs / overview.total_runs) * 100).toFixed(1)
    : 0;

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p className="dashboard-subtitle">Overview of your ETL pipelines and runs</p>
        </div>
        <button onClick={fetchDashboardData} className="btn-refresh">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 1.33334V4.00001M8 1.33334C4.3181 1.33334 1.33333 4.31811 1.33333 8.00001M8 1.33334C11.6819 1.33334 14.6667 4.31811 14.6667 8.00001M8 12V14.6667M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8.00001M8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8.00001" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-label">Total Flows</div>
            <div className="stat-icon">📊</div>
          </div>
          <div className="stat-value">{overview.total_flows}</div>
          <div className="stat-footer">
            <span className="stat-change positive">{overview.active_flows} active</span>
            <span className="stat-description">Pipelines configured</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-label">Total Runs</div>
            <div className="stat-icon">🔄</div>
          </div>
          <div className="stat-value">{overview.total_runs}</div>
          <div className="stat-footer">
            <span className="stat-change">{overview.running_jobs} running</span>
            <span className="stat-description">Executions recorded</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-label">Success Rate</div>
            <div className="stat-icon">✅</div>
          </div>
          <div className="stat-value">{successRate}%</div>
          <div className="stat-footer">
            <div className="stat-success-bar" style={{ width: `${successRate}%` }}></div>
            <span className="stat-change positive">+{successRate}%</span>
            <span className="stat-description">Total run performance</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-label">Failed Runs</div>
            <div className="stat-icon">❌</div>
          </div>
          <div className="stat-value">{overview.failed_runs}</div>
          <div className="stat-footer">
            <div className="stat-error-bar" style={{ width: `${failureRate}%` }}></div>
            <span className="stat-change negative">-{failureRate}%</span>
            <span className="stat-description">Failure rate of total</span>
          </div>
        </div>
      </div>

      {/* Recent Runs Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <div>
            <h2>Recent Runs</h2>
            <p className="section-subtitle">Latest ETL executions</p>
          </div>
          <div className="section-actions">
            <div className="status-filter-chips">
              <button
                className={`filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-chip ${statusFilter === 'completed' ? 'active' : ''}`}
                onClick={() => setStatusFilter('completed')}
              >
                Completed
              </button>
              <button
                className={`filter-chip ${statusFilter === 'failed' ? 'active' : ''}`}
                onClick={() => setStatusFilter('failed')}
              >
                Failed
              </button>
              <button
                className={`filter-chip ${statusFilter === 'running' ? 'active' : ''}`}
                onClick={() => setStatusFilter('running')}
              >
                Running
              </button>
            </div>
            <button onClick={() => navigate('/runs')} className="btn-link">
              View All →
            </button>
          </div>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Run ID</th>
                <th>Flow</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Records</th>
                <th>Triggered By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overview.recent_runs && overview.recent_runs.length > 0 ? (
                overview.recent_runs
                  .filter((run) => {
                    if (statusFilter === 'all') return true;
                    if (statusFilter === 'running') {
                      return ['pending', 'extracting', 'uploading', 'processing', 'loading'].includes(run.status);
                    }
                    return run.status === statusFilter;
                  })
                  .map((run) => (
                  <tr key={run.id}>
                    <td><code>{run.run_id}</code></td>
                    <td>{run.flow_name}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(run.status)}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="date-cell">{formatDate(run.started_at)}</td>
                    <td>{formatDuration(run.duration_seconds)}</td>
                    <td>
                      {run.records_extracted
                        ? `${run.records_loaded || 0} / ${run.records_extracted}`
                        : 'N/A'}
                    </td>
                    <td>{run.triggered_by_username || 'System'}</td>
                    <td>
                      <button
                        onClick={() => navigate(`/runs/${run.id}`)}
                        className="btn-small"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">No recent runs</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ETLDashboard;
